import * as React from 'react';
import isNil from 'lodash/isNil';
import { navigate } from 'libs/router';
import { Provider } from 'react-redux';
import { getStore } from 'redux-store/store';

import ContentCardContext from 'components/cards/content-card/context/ContentCardContext';
import OnsiteContentCard from 'components/cards/onsite-content-card/OnsiteContentCard';

import { abbrNum, getMetricValueInPercentage } from 'utils/helper/common';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST as IG_POST_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_REEL as IG_REEL_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_STORY as IG_STORY_TYPE,
  DELIVERABLE_TYPE_TIKTOK_VIDEO as TT_VIDEO_TYPE,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO as YT_VIDEO_TYPE
} from 'utils/deliverables';
import { renderReactTooltipToElement } from 'components/graphs/echarts/util/echarts-react-tooltip';

import type { ChannelTypes } from 'types';
import type { DOMRect } from 'types/Dom.type';
import type { StatisticsCountry } from 'types/Statistics.type';
import type {
  ScatterChartFormatterParam,
  ScatterChartFormatterParamSize,
  ScatterChartTooltip
} from '../types';
import type {
  ReportingMetaInfoIGStorySnippetType
} from 'modules/reporting/types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

export const LIKES = 'likes';
export const COMMENTS = 'comments';
export const VIEWS = 'views';
export const SHARES = 'shares';
export const IMPRESSIONS = 'impressions';
export const STORIES_IMPRESSIONS = 'storiesImpressions';
export const EXCLAMATION = 'exclamation';
export const ENGAGEMENT_RATE = 'engagement-rate';

const TYPE_TO_CHANNEL: {
  [x: string]: ChannelTypes
} = {
  [IG_POST_TYPE]: 'instagram',
  [IG_STORY_TYPE]: 'instagram',
  [IG_REEL_TYPE]: 'instagram',
  [TT_VIDEO_TYPE]: 'tiktok',
  [YT_VIDEO_TYPE]: 'youtube'
};

export const getAudienceInfo = (stat?: StatisticsCountry | null): string => {
  if (isNil(stat)) {
    return '-';
  }

  return `${stat.code || ''} (${abbrNum(stat.reach, { decimal: 1 }, true)} | ${getMetricValueInPercentage(stat.percent)})`;
};

const handleNoPostClick = (campaignId: number) => {
  navigate.to(`/campaigns/${campaignId}/report/v2/content-metrics`);
};

export const getFormattedTooltipWithStore = (node: React.ReactElement, consents: ConsentReducerConsentsMap
): React.ReactElement => (
  <Provider store={getStore({
    consentReducer: {
      consents
    }
  })}
  >
    {node}
  </Provider>
);

export const getFormattedTooltip = (
  item: ScatterChartFormatterParam,
  campaignId: number,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
): React.ReactElement => (
  <ContentCardContext.Provider value={{ posts: [item.data] }}>
    <div className="scatter-chart__tooltip">
      <OnsiteContentCard
        key={item.data.id}
        item={{
          ...item.data,
          metrics: {
            ...item.data.metrics,
            engagementRate: item.data?.metrics?.engagementRateFollowers || 0
          }
        }}
        index={0}
        channel={TYPE_TO_CHANNEL[item.data.type]}
        creatorProfile={item.data.influencerProfile}
        onNoPostClick={() => handleNoPostClick(campaignId)}
        igStorySnippetType={igStorySnippetType}
      />
    </div>
  </ContentCardContext.Provider>
);

export const getTooltip = (
  campaignId: number,
  consents: ConsentReducerConsentsMap,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
): ScatterChartTooltip => ({
  trigger: 'item',
  enterable: true,
  transitionDuration: 0.4,
  triggerOn: 'none', // see comment in ScatterChart.js for more info
  padding: [0],
  textStyle: {
    fontFamily: 'Hind',
    color: '#3a3a3a',
    lineHeight: 20
  },
  extraCssText: 'box-shadow: none; border: 0;',
  position: (
    mousePosition: Array<number>,
    param: ScatterChartFormatterParam | undefined | null,
    dom: HTMLElement | undefined | null,
    rect: DOMRect | undefined | null,
    size: ScatterChartFormatterParamSize
  ): Array<number> => {
    const x = mousePosition[0] - (size.contentSize[0] / 2);
    const y = (mousePosition[1] - size.contentSize[1]) - 12;

    return [x, y];
  },

  formatter: (param: ScatterChartFormatterParam): HTMLElement => (
    renderReactTooltipToElement(
      'scatter',
      getFormattedTooltipWithStore(getFormattedTooltip(
        param,
        campaignId,
        igStorySnippetType
      ),
      consents)
    )
  )
});
